// CreateProject.tsx
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import { STATUSARRAY } from "../../_contstants/common";
import { getFile, slugify } from "../../_helper/helper";
import {
  createProject,
  getProjectByID,
  projectImageUpload,
  updateProject,
} from "./services/project.services";
import { clearProjectDetails } from "./projectSlice";
import { getAllActivePropertyTypes } from "../propertytype/services/propertyType.service";
import { getAllActivePropertyStatuses } from "../propertystatus/services/propertyStatus.service";
import { getAllActiveAreas } from "../area/services/area.service";

const CreateProject: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const ProjectID = location.state?.ProjectID;

  const ProjectDetail = useAppSelector((state) => state.project.ProjectDetail);
  const loading = useAppSelector((state) => state.project.formLoading);
  const [formKey, setFormKey] = useState(1);

  const propertyTypes = useAppSelector(
    (state) => state.propertyType?.PropertyTypeList,
  );
  const propertyStatuses = useAppSelector(
    (state) => state.propertyStatus?.PropertyStatusList,
  );
  const areas = useAppSelector((state) => state.area?.AreaList);

  useEffect(() => {
    setFormKey((prev) => prev + 1);
  }, [ProjectDetail]);

  useEffect(() => {
    dispatch(getAllActivePropertyTypes());
    dispatch(getAllActivePropertyStatuses());
    dispatch(getAllActiveAreas());
    if (ProjectID) {
      dispatch(getProjectByID(ProjectID));
    }
    return () => {
      dispatch(clearProjectDetails());
    };
  }, [ProjectID]);

  const handleSubmit = async (values: any) => {
    const MainImage =
      values?.MainImage && typeof values?.MainImage !== "string"
        ? getFile(values?.MainImage[0] ?? values?.MainImage)
        : "";
    const BannerImage =
      values?.BannerImage && typeof values?.BannerImage !== "string"
        ? getFile(values?.BannerImage[0] ?? values?.BannerImage)
        : "";

    const hasNewImages = MainImage || BannerImage;

    const payload = {
      Title: values.Title ?? "",
      URL: values.URL ?? "",
      Tagline: values.Tagline ?? "",
      PropertyTypeID: values.PropertyTypeID ?? null,
      PropertyStatusID: values.PropertyStatusID ?? null,
      AreaID: values.AreaID ?? null,
      ShortDescription: values.ShortDescription ?? "",
      LongDescription: values.LongDescription ?? "",
      SquareFoot: values.SquareFoot ?? "",
      PriceRange: values.PriceRange ?? "",
      SequenceNo: values.SequenceNo ? Number(values.SequenceNo) : 0,
      IsDisplayInHome: values.IsDisplayInHome ?? false,
      MetaTitle: values.MetaTitle ?? "",
      MetaKeywords: values.MetaKeywords ?? "",
      MetaDescription: values.MetaDescription ?? "",
      IsActive: values.IsActive === 1,
    };

    const uploadImages = async (id: number) => {
      if (!hasNewImages) return;
      const imageData = new FormData();
      imageData.append("ID", String(id));
      if (MainImage) imageData.append("MainImage", MainImage);
      if (BannerImage) imageData.append("BannerImage", BannerImage);
      await dispatch(projectImageUpload(imageData));
    };

    try {
      if (ProjectID) {
        const response = await dispatch(
          updateProject({ ...payload, ID: ProjectID }),
        );
        if (response?.meta?.requestStatus === "fulfilled") {
          await uploadImages(ProjectID);
          dispatch(getProjectByID(ProjectID));
          navigate("/project");
        }
      } else {
        const response = await dispatch(createProject(payload));
        const newID = response?.payload?.Data?.ID;
        if (response?.meta?.requestStatus === "fulfilled" && newID) {
          await uploadImages(newID);
          navigate("/project");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    formRenderProps.onChange("URL", { value: slugify(e.value) });
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        key={formKey}
        initialValues={ProjectDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 30 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                ]}
              >
                {/* ── Header ── */}
                <GridLayoutItem colSpan={3}>
                  <Typography.h4 style={{ margin: 0 }}>
                    {ProjectID ? "Update Project" : "Add Project"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "flex-start",
                    gap: 10,
                  }}
                >
                  <ButtonWithLoading
                    label={ProjectID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    onClick={() => navigate("/project")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>

                {/* ── Section: Basic Info ── */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: 8,
                      marginTop: 20,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Basic Information
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={3}>
                  <Field
                    name="Title"
                    label="Title"
                    placeholder="Project Title"
                    onChange={(e: any) => handleCreateSlug(e, formRenderProps)}
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="SequenceNo"
                    label="Sequence No"
                    placeholder="e.g., 1"
                    component={FormTextField}
                    type="number"
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={3}>
                  <Field
                    name="URL"
                    label="URL / Slug"
                    placeholder="auto-generated-from-title"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    width: "100%",
                    margin: "18px 0px 0px",
                    padding: "0px 10px 8px",
                    fontWeight: 700,
                    background: "whitesmoke",
                    borderRadius: 5,
                    height: 48,
                  }}
                >
                  <Field
                    name="IsDisplayInHome"
                    label="Display in Home"
                    component={FormCheckbox}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={3}>
                  <Field
                    name="Tagline"
                    label="Tagline"
                    placeholder="Short catchy tagline"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={1}>
                  <Field
                    name="IsActive"
                    label="Status"
                    placeholder="Select status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => ({
                      value: status.value,
                      label: status.label,
                    }))}
                  />
                </GridLayoutItem>

                {/* ── Section: Property Details ── */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: 8,
                      marginTop: 30,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Property Details
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="PropertyTypeID"
                    label="Property Type"
                    placeholder="Select type"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    isAddNew={true}
                    drawerName="propertytypeform"
                    astrike={true}
                    options={propertyTypes.map((t: any) => ({
                      value: t.ID,
                      label: t.Title,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="PropertyStatusID"
                    label="Property Status"
                    placeholder="Select status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    isAddNew={true}
                    drawerName="propertystatusform"
                    astrike={true}
                    options={propertyStatuses.map((s: any) => ({
                      value: s.ID,
                      label: s.Title,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="AreaID"
                    label="Area"
                    placeholder="Select area"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    isAddNew={true}
                    drawerName="areaform"
                    astrike={true}
                    options={areas.map((a: any) => ({
                      value: a.ID,
                      label: a.Title,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="PriceRange"
                    label="Price Range"
                    placeholder="e.g., ₹50L – ₹1.2Cr"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={1}>
                  <Field
                    name="SquareFoot"
                    label="Square Foot"
                    placeholder="e.g., 1200 sq.ft"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                {/* ── Section: Descriptions ── */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: 8,
                      marginTop: 30,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Content
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={4}>
                  <Field
                    name="ShortDescription"
                    label="Short Description"
                    placeholder="Brief summary of the project"
                    component={FormTextArea}
                    rows={3}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={4}>
                  <Field
                    name="LongDescription"
                    label="Long Description"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>

                {/* ── Section: Images ── */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: 8,
                      marginTop: 30,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Images
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem
                  colSpan={4}
                  style={{ display: "flex", gap: 20 }}
                >
                  <Field
                    justifyContent="start"
                    name="MainImage"
                    label="Main Image"
                    alternateName="Main Image"
                    component={FormImageUpload}
                  />
                  <Field
                    justifyContent="start"
                    name="BannerImage"
                    label="Banner Image"
                    alternateName="Banner Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: 8,
                      marginTop: 30,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      SEO / Meta
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={4}>
                  <Field
                    name="MetaTitle"
                    label="Meta Title"
                    placeholder="Meta Title"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={4}>
                  <Field
                    name="MetaKeywords"
                    label="Meta Keywords"
                    placeholder="keyword1, keyword2, keyword3"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={4}>
                  <Field
                    name="MetaDescription"
                    label="Meta Description"
                    placeholder="Meta Description"
                    component={FormTextArea}
                    style={{ height: 100 }}
                  />
                </GridLayoutItem>

                {/* ── Footer Buttons ── */}
                <GridLayoutItem
                  colSpan={4}
                  style={{
                    marginTop: 20,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 10,
                  }}
                >
                  <ButtonWithLoading
                    label={ProjectID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    onClick={() => navigate("/project")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateProject;
